import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { posService } from '@/services/pos.service';
import { mapDetailToPosVariant } from '../utils/mapVariantDetail';
import { buildPromotionGiftOrderItem } from '../utils/giftItems';
import { useGiftVariantPicker } from './useGiftVariantPicker';
import type { FulfillmentMode, OrderItem } from '../types';
import type { PromotionFreeItem } from '../components/PromotionGiftBanner';

type SetOrderItems = (updater: OrderItem[] | ((prev: OrderItem[]) => OrderItem[])) => void;

interface UsePromotionGiftAddParams {
  selectedStoreId: string | undefined;
  fulfillmentMode: FulfillmentMode;
  orderItems: OrderItem[];
  promotionFreeItems: PromotionFreeItem[];
  setOrderItems: SetOrderItems;
  /**
   * TCR-1403 — tập `promotion_id` còn hợp lệ theo evaluate MỚI NHẤT (từ
   * `CustomerSidebar.onAppliedPromotionIdsChange`). `null`/không truyền =
   * CHƯA CÓ tín hiệu chắc chắn (giỏ đang đánh giá lại/stale, hoặc CTKM tự
   * động đang tắt) — không suy luận gì, giữ nguyên giỏ. Dùng để tự gỡ quà
   * CTKM (`gift_source: 'promotion'`) mồ côi — còn nằm trong giỏ nhưng
   * `gift_promotion_id` của nó không còn trong tập này nữa (vd sản phẩm điều
   * kiện vừa bị xoá). Quà VOUCHER (`gift_source: 'voucher'`) không đụng tới —
   * đường xử lý riêng của nó vẫn ở `removeItem`.
   */
  appliedPromotionIds?: string[] | null;
}

/**
 * Task 6/7 fix round 1 (I4) — `handleAddPromotionGift` + `pendingGifts` từng
 * chép tay HAI lần giống hệt nhau ở `POS/index.tsx` và `Orders/edit.tsx` (bản
 * `Orders/edit.tsx` còn thiếu `setOrderItems` trong dep array của
 * `useCallback` — mầm lỗi closure cũ, biến mất khi gộp về MỘT khai báo duy
 * nhất ở đây). Gộp lại để mọi fix sau này (vd M2) chỉ cần sửa một chỗ, không
 * thể lệch nhau giữa hai màn.
 *
 * Tái dùng đúng đường mà voucher `free_gift` đang dùng (`VoucherModal`):
 * `posService.listVariantsByProduct` lấy biến thể theo kho của ca, lọc theo
 * `is_sellable`/`stock_quantity`, mở `GiftVariantPickerDialog`
 * (`useGiftVariantPicker`) khi có nhiều lựa chọn, rồi `posService.lookupVariants`
 * lấy chi tiết biến thể đã chọn để dựng `PosVariant` qua `mapDetailToPosVariant`.
 */
export function usePromotionGiftAdd({
  selectedStoreId,
  fulfillmentMode,
  orderItems,
  promotionFreeItems,
  setOrderItems,
  appliedPromotionIds = null,
}: UsePromotionGiftAddParams) {
  const [pending, setPending] = useState(false);
  const { request: giftPickerRequest, pickGiftVariant, resolvePick } = useGiftVariantPicker();

  // TCR-1403: gỡ quà CTKM mồ côi. `removeItem` (POS/index.tsx, Orders/edit.tsx)
  // không tự cascade được lúc xoá sản phẩm điều kiện — nó không biết dòng nào
  // là điều kiện của CTKM nào (điều kiện có thể theo nhiều SKU/OR). Effect này
  // bù lại bằng tín hiệu ĐÃ CHỐT từ evaluate mới nhất: `appliedPromotionIds ===
  // null` nghĩa là chưa biết chắc (đang debounce/đánh giá lại) nên không làm
  // gì — chỉ hành động khi có một kết quả rõ ràng.
  useEffect(() => {
    if (appliedPromotionIds === null) return;
    const validIds = new Set(appliedPromotionIds);
    const isOrphanedGift = (item: OrderItem) =>
      item.gift_source === 'promotion' && !!item.gift_promotion_id && !validIds.has(item.gift_promotion_id);
    if (!orderItems.some(isOrphanedGift)) return;
    setOrderItems((prev) => prev.filter((item) => !isOrphanedGift(item)));
  }, [appliedPromotionIds, orderItems, setOrderItems]);

  // Quà đã nằm trong giỏ thì không hiện banner nữa.
  //
  // Fix round 1 (M2): so theo `gift_source === 'promotion'`, KHÔNG chỉ
  // `is_gift` thô. `is_gift` đúng cho CẢ quà voucher lẫn quà CTKM — so lỏng
  // như vậy dựa vào giả định "hai không gian giá trị của `gift_promotion_id`
  // không giao nhau" (đúng trong thực tế, nhưng không phải bất biến được kiểu
  // hoá) để ẩn banner đúng. `gift_source` là field TƯỜNG MINH, không phụ
  // thuộc giả định hình dạng chuỗi — dùng nó nhất quán với kỷ luật vừa dựng ở
  // `isVoucherGiftItem`.
  const pendingGifts = promotionFreeItems.filter(
    (f) =>
      // TCR-1074 (final review, Critical 2) — CHỈ dựng banner cho quà tặng
      // HẲN. `BxgyGetItem.discount_percent` nhận [0,100] và engine tôn trọng
      // nó, nhưng `buildPromotionGiftOrderItem` dựng dòng `price: 0` cứng ⇒
      // hiện banner cho một CTKM "mua X, mua Y giảm 50%" là biến nó thành
      // "tặng Y miễn phí", lỗi tiền thật.
      //
      // Chọn cách "ẩn" thay vì "định giá dòng theo % thật" cho đợt này: định
      // giá kéo theo cả pipeline tiền (`groupAndAllocateVat`, tổng đơn), cổng
      // RBAC `pos.discount` (dòng có giá ≠ giá gốc là `hasOverride`) và hiển
      // thị — rủi ro lệch tiền cao hơn nhiều so với việc im lặng bỏ sót một
      // loại CTKM chưa từng chạy được ở POS.
      //
      // So sánh NGHIÊM (`=== 100`, không `?? 100`): thiếu field = snapshot
      // khuyến mãi cũ trong Redis = KHÔNG BIẾT mức giảm. Đoán 100% là đoán về
      // phía có lợi cho khách. BE đóng dấu quà cũng fail-closed y hệt
      // (`resolvePromotionGiftClaim`), nên hai bên nhất quán: entry nào FE
      // không hiện banner thì BE cũng không đóng dấu.
      f.discount_percent === 100 &&
      !orderItems.some(
        (i) => i.gift_source === 'promotion' && i.gift_promotion_id === f.source_promotion_id,
      ),
  );

  const addPromotionGift = useCallback(
    async (freeItem: PromotionFreeItem) => {
      if (!selectedStoreId) {
        message.error('Chưa chọn cửa hàng, không tra được kho quà tặng.');
        return;
      }
      setPending(true);
      try {
        const options = await posService.listVariantsByProduct(freeItem.product_id, selectedStoreId);
        // TCR-1369 — CTKM tặng ĐÍCH DANH một biến thể thì chỉ xét đúng biến
        // thể đó. Xét cả họ biến thể của sản phẩm sẽ vừa hỏi thu ngân một câu
        // mà CTKM đã trả lời, vừa có thể phát nhầm biến thể đắt hơn khi biến
        // thể được tặng hết hàng.
        const candidates = freeItem.variant_id
          ? options.filter((o) => o.variant_id === freeItem.variant_id)
          : options;
        const eligible = candidates.filter(
          (o) => o.is_sellable && o.stock_quantity >= freeItem.quantity,
        );
        if (eligible.length === 0) {
          // Quà hết hàng KHÔNG huỷ chương trình — thu ngân vẫn bán đơn bình
          // thường, chỉ là không có quà để giao.
          message.warning(
            `Quà "${freeItem.product_name ?? ''}" đã hết hàng tại kho này. Đơn vẫn bán bình thường.`,
          );
          return;
        }
        const chosen =
          eligible.length === 1
            ? eligible[0]
            : await pickGiftVariant(freeItem.product_name ?? 'Quà tặng', freeItem.quantity, eligible);
        if (!chosen) return;

        const { items: detailItems, item_errors } = await posService.lookupVariants(
          [chosen.variant_code],
          fulfillmentMode,
        );
        if (item_errors.length > 0 || detailItems.length === 0) {
          message.error('Không tải được chi tiết sản phẩm quà tặng.');
          return;
        }

        const giftItem = buildPromotionGiftOrderItem(
          mapDetailToPosVariant(detailItems[0]),
          freeItem.quantity,
          freeItem.source_promotion_id,
          fulfillmentMode,
          uuidv4,
        );
        setOrderItems((prev) => [...prev, giftItem]);
      } catch {
        message.error('Không thêm được quà tặng. Vui lòng thử lại.');
      } finally {
        setPending(false);
      }
    },
    [selectedStoreId, fulfillmentMode, pickGiftVariant, setOrderItems],
  );

  return { pending, addPromotionGift, pendingGifts, giftPickerRequest, resolvePick };
}
